import React, { useState, useEffect } from 'react';
import { FileText, Calendar, User, ArrowRight, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null); // Para ver post completo

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      // Obtener posts del gestor de contenido unificado
      const response = await axios.get(`${BACKEND_URL}/api/content?category=blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(posts.map(post => post.blog_category || post.category).filter(Boolean))];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || (post.blog_category || post.category) === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{fontFamily: 'Syne, sans-serif'}} data-testid="blog-title">
            Blog
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Artículos sobre fitness, nutrición y bienestar para ayudarte en tu transformación
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar artículos..."
              className="pl-12 rounded-full py-6"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === cat
                    ? 'bg-black text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-200'
                }`}
              >
                {cat === 'all' ? 'Todos' : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No hay artículos disponibles</h3>
            <p className="text-zinc-500">
              {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'Pronto publicaremos nuevo contenido'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.content_id || post.post_id} 
                className="bg-zinc-900 rounded-3xl overflow-hidden border border-zinc-800 hover:shadow-xl transition-shadow border border-zinc-100"
              >
                {/* Imagen */}
                <div className="aspect-video bg-gradient-to-br from-pink-100 to-pink-50 relative overflow-hidden">
                  {(post.cover_image || post.image_url) ? (
                    <img 
                      src={(post.cover_image || post.image_url).startsWith('/api') ? `${BACKEND_URL}${post.cover_image || post.image_url}` : (post.cover_image || post.image_url)} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-16 h-16 text-pink-300" />
                    </div>
                  )}
                  {(post.blog_category || post.category) && (
                    <span className="absolute top-4 left-4 bg-black text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                      {post.blog_category || post.category}
                    </span>
                  )}
                </div>

                {/* Contenido */}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-zinc-500 mb-3">
                    {post.author && (
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author}
                      </span>
                    )}
                    {post.created_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.created_at).toLocaleDateString('es-ES', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                    )}
                  </div>

                  <h2 className="text-xl font-bold mb-3" style={{fontFamily: 'Syne, sans-serif'}}>
                    {post.title}
                  </h2>

                  <p className="text-zinc-400 mb-4 line-clamp-3">
                    {(post.description || post.content)?.substring(0, 150)}...
                  </p>

                  <Button 
                    variant="outline" 
                    className="w-full rounded-full border-2 border-black text-black hover:bg-black hover:text-white"
                    onClick={() => setSelectedPost(post)}
                  >
                    Leer más
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-zinc-950 text-white rounded-3xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{fontFamily: 'Syne, sans-serif'}}>
            ¿Quieres más contenido exclusivo?
          </h2>
          <p className="text-xl text-zinc-400 mb-6">
            Únete a nuestra comunidad y recibe artículos exclusivos directamente en tu correo
          </p>
          <Button className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-12 py-6 text-lg font-bold">
            Ver Programas
          </Button>
        </div>
      </div>

      {/* Modal para ver post completo */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8">
            {/* Header del modal */}
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-100 p-6 flex justify-between items-center">
              <div>
                {selectedPost.category && (
                  <span className="text-xs font-bold text-pink-600 uppercase">{selectedPost.category}</span>
                )}
              </div>
              <button 
                onClick={() => setSelectedPost(null)}
                className="p-2 hover:bg-zinc-800 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Imagen del post */}
            {(selectedPost.image_url || selectedPost.cover_image) && (
              <div className="w-full h-64 md:h-96 overflow-hidden">
                <img 
                  src={(selectedPost.cover_image || selectedPost.image_url).startsWith('http') 
                    ? (selectedPost.cover_image || selectedPost.image_url)
                    : `${BACKEND_URL}${selectedPost.cover_image || selectedPost.image_url}`}
                  alt={selectedPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Contenido del post */}
            <div className="p-8">
              <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
                {selectedPost.author && (
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedPost.author}
                  </span>
                )}
                {selectedPost.created_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedPost.created_at).toLocaleDateString('es-ES', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold mb-6" style={{fontFamily: 'Syne, sans-serif'}}>
                {selectedPost.title}
              </h1>

              <div className="prose prose-lg max-w-none">
                {selectedPost.content?.split('\n').map((paragraph, idx) => (
                  <p key={idx} className="text-zinc-300 mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
